import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemHealthLevel, type ItemHealthRiskType } from '@/common/api/types/dashboard/health';
import { getHealthLevelLabel } from '@/features/insights-dashboard/utils/translation-resolvers/getHealthLevelLabel';
import { getHealthRiskTypeTitle } from '@/features/insights-dashboard/utils/translation-resolvers/getHealthRiskTypeTitle';
import { HealthBadge } from './HealthBadge';

interface HealthBadgeWithTooltipProps {
  healthLevel: ItemHealthLevel;
  healthTypes: ItemHealthRiskType[];
  isTotal?: boolean;
  className?: string;
}

export const HealthBadgeWithTooltip = ({
  healthLevel,
  healthTypes,
  isTotal = false,
  className,
}: HealthBadgeWithTooltipProps) => {
  const { t } = useTranslation();
  const label = getHealthLevelLabel(healthLevel, t);
  const badge = <HealthBadge healthLevel={healthLevel} label={label} className={className} />;

  let content: ReactNode = null;
  if (healthLevel === ItemHealthLevel.DONE) {
    content = t(($) => $.insights.ragStatus.DONE_tooltip);
  } else if (healthTypes.length > 0) {
    content = (
      <div className="space-y-2">
        <div className="font-semibold">
          {isTotal
            ? t(($) => $.insights.workAllocation.columnHeaders.riskCategoriesTitlePlural)
            : t(($) => $.insights.workAllocation.columnHeaders.riskCategoriesTitle)}
        </div>
        <ul className="space-y-1 list-disc list-inside">
          {healthTypes.map((healthType) => (
            <li key={healthType}>{getHealthRiskTypeTitle(healthType, t)}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (content === null) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex max-w-full">{badge}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm" side="top" sideOffset={8}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};
